import { useEffect, useRef, useState } from 'react'
import { History, MessageSquarePlus, Sigma, Trash2 } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  ERRO,
  calcular,
  digitar,
  inserirNumero,
  numeroAtual,
  paraExibicao,
  sanear,
} from '@/lib/calculadora'

/**
 * Calculadora das acoes rapidas (modulo "calculator", fase 4 de
 * docs/PLANO_FEEDBACK_ADRIAN.md).
 *
 * A conta e montada inteira e so calculada no "=", na ordem certa das
 * operacoes — 2 + 3 x 4 = 14. O visor tem duas linhas: em cima a expressao
 * como esta sendo escrita, embaixo o numero em digitacao, que vira o
 * resultado quando se aperta "=".
 *
 * Esta tela nao faz conta: monta a expressao e a entrega a `@/lib/calculadora`
 * (que avalia com o expr-eval). 100% client-side: nenhuma chamada ao servidor.
 */

const MAX_HISTORICO = 5
// lembra se a fileira de Fisica fica aberta (preferencia de quem usa o aparelho)
const CHAVE_FISICA = 'edurpg.calculadora.fisica'

// teclas que continuam a conta a partir do resultado, em vez de comecar outra
const CONTINUAM = ['+', '−', '×', '÷', '²', '%']

interface ContaFeita {
  conta: string
  resultado: string
}

interface CalculatorSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Leva a conta para a caixa de mensagem do chat (nao envia sozinho: quem
   * escreve decide, e as regras de turno seguem valendo no envio).
   */
  onLevarParaChat?: (texto: string) => void
}

export function CalculatorSheet({ open, onOpenChange, onLevarParaChat }: CalculatorSheetProps) {
  const [expressao, setExpressao] = useState('')
  // null enquanto a conta nao foi fechada pelo "="
  const [resultado, setResultado] = useState<string | null>(null)
  const [historico, setHistorico] = useState<ContaFeita[]>([])
  const [verHistorico, setVerHistorico] = useState(false)
  const [verFisica, setVerFisica] = useState(() => {
    try {
      return localStorage.getItem(CHAVE_FISICA) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_FISICA, verFisica ? '1' : '0')
    } catch {
      // navegacao privada: so nao lembra da preferencia
    }
  }, [verFisica])

  const limpar = () => {
    setExpressao('')
    setResultado(null)
  }

  /**
   * Uma tecla qualquer. Com a conta ja fechada, operacao continua de onde
   * parou (14 x ...) e numero comeca conta nova — que e o que se espera de
   * uma calculadora depois do "=".
   */
  const tecla = (t: string) => {
    if (resultado === null) {
      setExpressao(exp => digitar(exp, t))
      return
    }
    if (t === '⌫') {
      // volta a editar a conta anterior em vez de apagar o resultado
      setResultado(null)
      return
    }
    const base = CONTINUAM.includes(t) && resultado !== ERRO ? resultado : ''
    setExpressao(digitar(base, t))
    setResultado(null)
  }

  const igual = () => {
    if (resultado !== null) return
    const limpa = sanear(expressao)
    if (limpa === '') return
    const texto = calcular(limpa)
    setExpressao(limpa)
    setResultado(texto)
    if (texto !== ERRO) {
      setHistorico(anterior => [
        { conta: paraExibicao(limpa), resultado: texto },
        ...anterior,
      ].slice(0, MAX_HISTORICO))
    }
  }

  /** Toca numa conta do historico para reaproveitar o resultado dela. */
  const reusar = (feita: ContaFeita) => {
    setExpressao(exp => inserirNumero(resultado === null ? exp : '', feita.resultado))
    setResultado(null)
    setVerHistorico(false)
  }

  // Teclado fisico enquanto a calculadora esta aberta (no computador do
  // professor, digitar e mais rapido que clicar). O ref e reatribuido a cada
  // render para o listener sempre enxergar o estado atual sem reassinar.
  const acaoDaTecla = useRef<(k: string) => boolean>(() => false)
  useEffect(() => {
    acaoDaTecla.current = (k: string) => {
      if (/^[0-9.]$/.test(k)) { tecla(k); return true }
      switch (k) {
        case ',': tecla('.'); return true
        case '+': tecla('+'); return true
        case '-': tecla('−'); return true
        case '*': case 'x': case 'X': tecla('×'); return true
        case '/': tecla('÷'); return true
        case '(': case ')': tecla(k); return true
        case '%': tecla('%'); return true
        case '^': tecla('²'); return true
        case '=': case 'Enter': igual(); return true
        case 'Backspace': tecla('⌫'); return true
        case 'c': case 'C': case 'Delete': limpar(); return true
        default: return false
      }
    }
  })

  useEffect(() => {
    if (!open) return
    const aoTeclar = (e: KeyboardEvent) => {
      // Escape fica com o drawer (fecha a calculadora); atalhos do navegador passam
      if (e.ctrlKey || e.metaKey || e.altKey || e.key === 'Escape') return
      if (acaoDaTecla.current(e.key)) e.preventDefault()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [open])

  const contaVisivel = resultado === null
    ? paraExibicao(expressao)
    : `${paraExibicao(expressao)} =`
  const numeroVisivel = resultado ?? numeroAtual(expressao)
  const textoDaConta = resultado === null
    ? paraExibicao(expressao) || numeroVisivel
    : `${paraExibicao(expressao)} = ${resultado}`

  const teclaOp = 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-400'
  const teclaNum = 'bg-game-tertiary dark:bg-zinc-800/50 hover:bg-game-hover dark:hover:bg-zinc-700 text-game-primary dark:text-white'
  const teclaFisica = 'bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 h-11 text-base'

  const botao = (
    label: string,
    onClick: () => void,
    extra?: string,
    rotulo?: string,
  ) => (
    <Button
      key={label}
      type="button"
      variant="ghost"
      onClick={onClick}
      aria-label={rotulo}
      className={cn(
        'h-14 rounded-xl text-lg font-semibold border border-game-primary transition-all',
        'active:scale-[0.97]',
        extra ?? teclaNum,
      )}
    >
      {label}
    </Button>
  )

  const botaoTecla = (t: string, extra?: string, rotulo?: string) =>
    botao(t, () => tecla(t), extra, rotulo)

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="bg-game-secondary border-t border-game-primary max-h-[92vh]"
        data-vaul-drawer-direction="bottom"
      >
        <DrawerHeader className="text-left pb-2 pt-2">
          <div className="flex items-center justify-between gap-2">
            <DrawerTitle className="text-game-primary text-xl font-semibold">
              Calculadora
            </DrawerTitle>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Histórico de contas"
                aria-pressed={verHistorico}
                disabled={historico.length === 0}
                onClick={() => setVerHistorico(v => !v)}
                className={cn(
                  'h-9 w-9 p-0 rounded-xl',
                  verHistorico ? 'bg-game-tertiary text-game-primary dark:text-white' : 'text-game-secondary',
                )}
              >
                <History className="h-4 w-4" />
              </Button>
              {onLevarParaChat && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label="Levar a conta para o chat"
                  disabled={resultado === ERRO}
                  onClick={() => {
                    onLevarParaChat(textoDaConta)
                    onOpenChange(false)
                  }}
                  className="h-9 w-9 p-0 rounded-xl text-sky-400 hover:bg-sky-500/10"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <DrawerDescription className="text-game-secondary text-xs mt-1">
            A conta inteira aparece no visor e só é calculada no "=". No computador dá para digitar pelo teclado.
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 pb-8 max-w-sm w-full mx-auto">
          {verHistorico && historico.length > 0 && (
            <div
              data-testid="calc-historico"
              className="mb-2 rounded-xl border border-game-primary bg-game-tertiary/60 dark:bg-zinc-900/60 p-1 max-h-28 overflow-y-auto"
            >
              {historico.map((feita, i) => (
                <button
                  key={`${feita.conta}-${i}`}
                  type="button"
                  onClick={() => reusar(feita)}
                  className="w-full rounded-lg px-2 py-1 text-right text-xs tabular-nums text-game-secondary hover:bg-game-hover dark:hover:bg-zinc-800"
                >
                  <span className="text-game-muted dark:text-zinc-500">{feita.conta} = </span>
                  <span className="font-semibold text-game-primary dark:text-white">{feita.resultado}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setHistorico([])
                  setVerHistorico(false)
                }}
                className="mt-1 flex w-full items-center justify-center gap-1 rounded-lg px-2 py-1 text-[11px] text-game-muted hover:bg-game-hover dark:hover:bg-zinc-800"
              >
                <Trash2 className="h-3 w-3" /> limpar histórico
              </button>
            </div>
          )}

          <div className="mb-3 rounded-xl border border-game-primary bg-game-tertiary dark:bg-zinc-900 px-4 py-2">
            <div
              data-testid="calc-conta"
              aria-live="polite"
              className="h-6 text-right text-sm font-medium tabular-nums text-amber-500 dark:text-amber-400 overflow-x-auto whitespace-nowrap"
            >
              {contaVisivel}
            </div>
            <div
              data-testid="calc-display"
              className="pb-1 text-right text-3xl font-bold tabular-nums text-game-primary dark:text-white overflow-x-auto"
            >
              {numeroVisivel}
            </div>
          </div>

          <div className="mb-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={verFisica}
              onClick={() => setVerFisica(v => !v)}
              className={cn(
                'h-8 gap-1 rounded-lg px-2 text-xs',
                verFisica ? 'bg-sky-500/10 text-sky-400' : 'text-game-secondary',
              )}
            >
              <Sigma className="h-3.5 w-3.5" />
              Física
            </Button>
          </div>

          {verFisica && (
            <div data-testid="calc-fisica" className="mb-2 grid grid-cols-4 gap-2">
              {botaoTecla('√', teclaFisica, 'Raiz quadrada')}
              {botaoTecla('²', teclaFisica, 'Elevar ao quadrado')}
              {botaoTecla('π', teclaFisica, 'Pi')}
              {botao('×10ˣ', () => tecla('e'), teclaFisica, 'Notação científica')}
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            {botao('C', limpar, 'bg-rose-500/10 hover:bg-rose-500/20 text-rose-400', 'Limpar')}
            {botaoTecla('⌫', teclaNum, 'Apagar')}
            {botao('( )', () => tecla('()'), teclaNum, 'Parênteses')}
            {botaoTecla('÷', teclaOp, 'Dividir')}

            {botaoTecla('7')}
            {botaoTecla('8')}
            {botaoTecla('9')}
            {botaoTecla('×', teclaOp, 'Multiplicar')}

            {botaoTecla('4')}
            {botaoTecla('5')}
            {botaoTecla('6')}
            {botaoTecla('−', teclaOp, 'Subtrair')}

            {botaoTecla('1')}
            {botaoTecla('2')}
            {botaoTecla('3')}
            {botaoTecla('+', teclaOp, 'Somar')}

            {botaoTecla('%', teclaNum, 'Porcentagem')}
            {botaoTecla('0')}
            {botaoTecla('.', teclaNum, 'Ponto decimal')}
            {botao('=', igual, 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400', 'Igual')}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
